import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

interface FormState {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const { t } = useTranslation(['translation', 'contact']);
  
  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState<Partial<FormState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (formErrors[name as keyof FormState]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const validateForm = (): boolean => {
    const errors: Partial<FormState> = {};
    
    if (!formState.name.trim()) {
      errors.name = t('contact:contact.errors.nameRequired');
    }
    
    if (!formState.email.trim()) {
      errors.email = t('contact:contact.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = t('contact:contact.errors.emailInvalid');
    }
    
    if (!formState.subject.trim()) {
      errors.subject = t('contact:contact.errors.subjectRequired');
    }
    
    if (!formState.message.trim()) {
      errors.message = t('contact:contact.errors.messageRequired');
    } else if (formState.message.length < 10) {
      errors.message = t('contact:contact.errors.messageTooShort');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Here would be the actual API call to submit the form
      // For example:
      // const response = await fetch('/api/contact', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(formState),
      // });
      
      // if (!response.ok) {
      //   throw new Error('Failed to submit form');
      // }
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reset form and show success message
      setFormState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitError(t('contact:contact.errors.submissionFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white min-h-screen">
      {/* Shared Header Component */}
      <Header />

      {/* Content with padding for fixed header */}
      <div className="pt-16">
        {/* Contact section */}
        <div className="py-16 px-4 overflow-hidden sm:px-6 lg:px-8 lg:py-24">
          <div className="relative max-w-xl mx-auto">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                {t('contact:contact.title')}
              </h2>
              <p className="mt-4 text-lg leading-6 text-gray-500">
                {t('contact:contact.subtitle')}
              </p>
            </div>
            
            <div className="mt-12">
              {submitSuccess ? (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Icon iconName="CheckCircle" className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        {t('contact:contact.successTitle')}
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>
                          {t('contact:contact.successMessage')}
                        </p>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setSubmitSuccess(false)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          {t('contact:contact.sendAnother')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
                  <div className="sm:col-span-2">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      {t('contact:contact.form.name')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={formState.name}
                        onChange={handleChange}
                        className={`py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md ${
                          formErrors.name ? 'border-red-300' : ''
                        }`}
                      />
                      {formErrors.name && (
                        <p className="mt-2 text-sm text-red-600" id="name-error">
                          {formErrors.name}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      {t('contact:contact.form.email')} *
                    </label>
                    <div className="mt-1">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formState.email}
                        onChange={handleChange}
                        className={`py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md ${
                          formErrors.email ? 'border-red-300' : ''
                        }`}
                      />
                      {formErrors.email && (
                        <p className="mt-2 text-sm text-red-600" id="email-error">
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      {t('contact:contact.form.phone')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="phone"
                        id="phone"
                        value={formState.phone}
                        onChange={handleChange}
                        className="py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                        placeholder="+1 (555) 987-6543"
                      />
                    </div>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                      {t('contact:contact.form.subject')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="subject"
                        id="subject"
                        value={formState.subject}
                        onChange={handleChange}
                        className={`py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md ${
                          formErrors.subject ? 'border-red-300' : ''
                        }`}
                      />
                      {formErrors.subject && (
                        <p className="mt-2 text-sm text-red-600" id="subject-error">
                          {formErrors.subject}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                      {t('contact:contact.form.message')} *
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={formState.message}
                        onChange={handleChange}
                        className={`py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md ${
                          formErrors.message ? 'border-red-300' : ''
                        }`}
                      ></textarea>
                      {formErrors.message && (
                        <p className="mt-2 text-sm text-red-600" id="message-error">
                          {formErrors.message}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {submitError && (
                    <div className="sm:col-span-2">
                      <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <Icon iconName="Error" className="h-5 w-5 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">{t('contact:contact.errors.title')}</h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>{submitError}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon iconName="HourglassEmpty" size={20} className="animate-spin -ml-1 mr-2 h-5 w-5" />
                          {t('contact:contact.form.submitting')}
                        </>
                      ) : (
                        t('contact:contact.form.submit')
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
            
            <div className="mt-16 bg-gray-50 p-8 rounded-lg shadow-md">
              <h3 className="text-lg font-medium text-gray-900">{t('contact:contact.officeLocations')}</h3>
              
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-base font-medium text-gray-900">{t('contact:contact.locations.toronto.name')}</h4>
                    <div className="mt-2 text-base text-gray-500 space-y-1">
                      <p>3600 Steeles Ave. E</p>
                      <p>Room E171, Markham</p>
                      <p>ON, L3R 9Z7</p>
                      <p>Canada</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-base font-medium text-gray-900">{t('contact:contact.locations.hongKong.name')}</h4>
                    <div className="mt-2 text-base text-gray-500 space-y-1">
                      <p>Unit 2A, 17/F, Glenealy Tower</p>
                      <p>No.1 Glenealy, Central</p>
                      <p>Hong Kong</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 border-t border-gray-200 pt-8">
                <h4 className="text-base font-medium text-gray-900">{t('contact:contact.emailUs')}</h4>
                <p className="mt-2 text-base text-gray-500">
                  <a href="mailto:info@agiliscare.com" className="text-indigo-600 hover:text-indigo-500">
                    info@agiliscare.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Contact; 